import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from '@tanstack/react-router';

import { useProjectStore } from '../stores/project';
import { shaktiApi } from '../api/client';

/**
 * N5.3 (round-6) — Real dashboard home.
 *
 * Previously this route was a "Wave 2 surface coming soon" placeholder
 * that shipped in production. The new version is a project-summary
 * home: active packets count, latest phase, pending approvals count,
 * tokens consumed this period, and a recent activity feed.
 *
 * Data sources:
 *   GET /v1/projects/{id}/summary  (N1.8 rewrite)
 *   GET /v1/projects               (fallback if no project selected)
 *   GET /v1/approvals/count        (ambient badge data; best-effort)
 */
export function IndexRoute(): JSX.Element {
  const location = useLocation();
  const { activeProjectId, projects } = useProjectStore();

  // When we're not at `/`, preserve the legacy behaviour so nested
  // routes that haven't had a dedicated component written yet still
  // render something meaningful instead of a blank page.
  if (location.pathname !== '/') {
    return <LegacyPlaceholder pathname={location.pathname} />;
  }

  return <ProjectHome projectId={activeProjectId} projectCount={projects.length} />;
}

function ProjectHome({
  projectId,
  projectCount,
}: {
  projectId: string | null;
  projectCount: number;
}): JSX.Element {
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(!!projectId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    shaktiApi
      .getProjectSummary(projectId)
      .then((data) => {
        if (!cancelled) {
          setSummary(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!projectId) {
    return (
      <section aria-labelledby="home-title" className="mx-auto flex max-w-3xl flex-col gap-6">
        <header className="flex flex-col gap-2">
          <h1 id="home-title" className="text-2xl font-semibold tracking-tight text-fg-primary">
            Welcome to Shakti
          </h1>
          <p className="text-sm text-fg-secondary">
            {projectCount === 0
              ? 'Create your first project from the workspace switcher above to start building.'
              : 'Select a project from the workspace switcher above to see its status.'}
          </p>
        </header>
      </section>
    );
  }

  if (loading) {
    return (
      <section aria-busy="true" aria-live="polite" className="mx-auto max-w-5xl">
        <div className="shakti-card">Loading project summary…</div>
      </section>
    );
  }

  if (error || !summary) {
    return (
      <section aria-labelledby="home-title" className="mx-auto max-w-5xl">
        <div className="shakti-card text-status-error">
          Could not load project summary: {error ?? 'unknown error'}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="home-title" className="mx-auto flex max-w-5xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          Project {summary.name}
        </span>
        <h1 id="home-title" className="text-2xl font-semibold tracking-tight text-fg-primary">
          {summary.name}
        </h1>
        <p className="text-sm text-fg-secondary">
          {summary.description || 'Project summary and status overview.'}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Active packets" value={summary.active_packets.toString()} />
        <MetricCard
          label="Current phase"
          value={summary.latest_phase_name || '—'}
          sub={summary.latest_phase_number != null ? `P${summary.latest_phase_number}` : undefined}
        />
        <MetricCard
          label="Pending approvals"
          value={summary.pending_approvals.toString()}
          tone={summary.pending_approvals > 0 ? 'warn' : undefined}
          linkTo="/dashboard/approvals"
        />
        <MetricCard
          label="Tokens this period"
          value={formatTokens(summary.tokens_period)}
          sub={summary.cost_period_usd != null ? `$${summary.cost_period_usd.toFixed(2)}` : undefined}
        />
      </div>

      <article aria-labelledby="activity-heading" className="shakti-card flex flex-col gap-3">
        <header className="flex items-center justify-between">
          <h2 id="activity-heading" className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
            Recent activity
          </h2>
          <Link to="/dashboard/activity" className="text-xs text-accent">
            Open activity →
          </Link>
        </header>
        {summary.recent_activity.length === 0 ? (
          <p className="text-sm text-fg-secondary">No activity yet — kick off a build from the Command Center.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle">
            {summary.recent_activity.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-fg-secondary">{entry.action}</span>
                <time className="shakti-mono text-2xs text-fg-muted" dateTime={entry.occurred_at}>
                  {formatRelative(entry.occurred_at)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </article>
    </section>
  );
}

function MetricCard({
  label,
  value,
  sub,
  tone,
  linkTo,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'warn' | 'ok';
  linkTo?: string;
}): JSX.Element {
  const content = (
    <div
      className={`shakti-card flex flex-col gap-1 ${
        tone === 'warn' ? 'border-status-warning text-status-warning' : ''
      }`}
    >
      <span className="text-2xs uppercase tracking-wider text-fg-muted">{label}</span>
      <span className="text-2xl font-semibold text-fg-primary">{value}</span>
      {sub && <span className="text-xs text-fg-secondary">{sub}</span>}
    </div>
  );
  if (linkTo) {
    return (
      <Link to={linkTo} className="no-underline">
        {content}
      </Link>
    );
  }
  return content;
}

function LegacyPlaceholder({ pathname }: { pathname: string }): JSX.Element {
  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          {pathname}
        </span>
        <h1 className="text-2xl font-semibold tracking-tight text-fg-primary">
          Coming soon
        </h1>
        <p className="text-sm text-fg-secondary">
          This view is still being wired to live data. The home page (/) now shows
          the project summary.
        </p>
      </header>
    </section>
  );
}

function formatTokens(n: number | null | undefined): string {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const delta = Math.max(0, now - then) / 1000;
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  if (delta < 86_400) return `${Math.round(delta / 3600)}h ago`;
  return `${Math.round(delta / 86_400)}d ago`;
}

export interface ProjectSummary {
  id: string;
  name: string;
  description: string | null;
  active_packets: number;
  latest_phase_number: number | null;
  latest_phase_name: string | null;
  pending_approvals: number;
  tokens_period: number | null;
  cost_period_usd: number | null;
  recent_activity: Array<{ id: string; action: string; occurred_at: string }>;
}
